import { faPaperPlane, faTimes } from "@fortawesome/free-solid-svg-icons";
import { Button, DataTable, IconButton } from "@ventumsoftware/react-toolkit";
import type { FC } from "react";
import { useFetchedResource } from "../../api/APIContext";
import { useApiCall } from "../../api/api";
import type { Inspection } from "../../api/dto/Inspection";
import type { SharedInspectionFinding } from "../../api/dto/SharedInspectionFinding";
import { RevokeSharedInspectionFindingModal } from "../../modals/RevokeSharedInspectionFindingModal";
import { SendSharedInspectionFindingModal } from "../../modals/SendSharedInspectionFindingModal";
import { formatDateTime } from "../../util/formatTime";
import { Card } from "../Card";
import { TablePlaceholder } from "../data/TablePlaceholder";
import { Loading } from "../Loading";
import { useModal } from "../layout/ModalProvider";
import { useRefreshEffect } from "../RefreshController";
import type { EnrichedInspection } from "./types";

export const SharedInspectionFindingsOverview: FC<{
  inspection: EnrichedInspection;
  isCustomer: boolean;
}> = (props) => {
  const { listSharedInspectionFindings } = useApiCall();
  const sharedInspectionFindings = useFetchedResource((inspectionId: string) =>
    listSharedInspectionFindings(inspectionId),
  );
  useRefreshEffect(() => sharedInspectionFindings.reload(props.inspection.id));

  const sendModal = useModal({
    title: "Bevinding delen",
    body: (
      <SendSharedInspectionFindingModal
        inspection={props.inspection}
        default={
          [
            { name: props.inspection.contactName, email: props.inspection.contactEmail },
            {
              name: props.inspection.project.contactName,
              email: props.inspection.project.contactEmail,
            },
          ].filter(
            (x, i, all) =>
              x.email &&
              x.name &&
              !sharedInspectionFindings.resource?.find((s) => x.email === s.email) &&
              all.findIndex((y) => y.email === x.email) === i,
          ) as { name: string; email: string }[]
        }
        onFinish={() => sharedInspectionFindings.reload(props.inspection.id)}
      />
    ),
  });

  return (
    <Card title={"Gedeelde bevindingen"}>
      <Loading loading={sharedInspectionFindings.loading}>
        {(sharedInspectionFindings.resource ?? []).length === 0 ? (
          <TablePlaceholder>Geen bevindingen</TablePlaceholder>
        ) : (
          <DataTable
            keyProperty={"uuid"}
            data={sharedInspectionFindings.resource ?? []}
            pageSize={50}
            columns={[
              {
                property: "name",
                header: "Naam",
                sort: true,
              },
              {
                property: "email",
                header: "Email",
                sort: true,
              },
              {
                property: "sent_at",
                header: "Verzonden",
                transform: (value: Date | null) => formatDateTime(value),
              },
              {
                property: "opened_at",
                header: "Geopend",
                transform: (value: Date | null) => formatDateTime(value),
              },
              {
                property: "modified_at",
                header: "Aangepast",
                transform: (value: Date | null) => formatDateTime(value),
              },
              {
                property: "expires_at",
                header: "Status",
                transform: (value: Date | null) => {
                  if (!value) return "-";
                  const ok = value > new Date();
                  return (
                    <span
                      className={`inline-block rounded-full px-2 py-0.5 text-xs whitespace-nowrap ${ok ? "bg-green-100 text-green-800" : "bg-red-100 text-red-700"}`}
                    >
                      {ok ? "Geldig tot " : "Verlopen "}
                      {value.toLocaleString(
                        "nl",
                        ok
                          ? { weekday: "long", hour: "2-digit", minute: "2-digit" }
                          : { dateStyle: "short", timeStyle: "short" },
                      )}
                    </span>
                  );
                },
              },
              {
                property: "uuid",
                header: "Acties",
                transform: (_: unknown, value: SharedInspectionFinding) => (
                  <TableActions
                    inspection={props.inspection}
                    sharedInspectionFinding={value}
                    onFinish={() => sharedInspectionFindings.reload(props.inspection.id)}
                  />
                ),
              },
            ]}
          />
        )}
        <div className={"flex gap-2 mt-4"}>
          <Button
            type={"secondary"}
            icon={faPaperPlane}
            size={"md"}
            disabled={props.inspection.finishedAt === null}
            text={"Bevindingen delen"}
            onClick={sendModal.open}
          />
        </div>
      </Loading>
    </Card>
  );
};

const TableActions: FC<{
  inspection: Inspection;
  sharedInspectionFinding: SharedInspectionFinding;
  onFinish: () => void;
}> = (props) => {
  const revokeModal = useModal({
    title: "Link terugtrekken",
    body: (
      <RevokeSharedInspectionFindingModal
        inspection={props.inspection}
        sharedInspectionFinding={props.sharedInspectionFinding}
        onFinish={props.onFinish}
      />
    ),
  });
  return (
    <div className={"flex items-center justify-end"}>
      {props.sharedInspectionFinding.expires_at !== null &&
        props.sharedInspectionFinding.expires_at > new Date() && (
          <>
            <IconButton icon={faTimes} size={"md"} type={"danger"} onClick={revokeModal.open} />
          </>
        )}
    </div>
  );
};
